import { View, SxProp, useDripsyTheme, Image, Pressable } from 'dripsy';
import { useTranslation } from 'react-i18next';
import { useNavigation } from '@react-navigation/native';
import React, { useMemo, useState } from 'react';
import { H3, TextBody, H6, TextCaption, TextTitle, TextSmall } from '@/design-system/components/typography';
import { PageItem } from '@/features/earn/components/page/PageItem';
import { PageItemSkeleton } from '@/features/earn/components/PageItemSkeleton';

import { PageContainer } from '@/features/card-account/screens/page/PageContainer';
import { UniInput } from '@/design-system/components/inputs';
import { formatAmountByDecimals } from '@/utils/numberFormatting';
import { Pagination } from '@/design-system/components/pagination/Pagination';
import { Icon } from '@/design-system/components/icons/Icon';

import { useCardAccounts, AccountItem, CardItem } from '@/features/card-account/hooks/useCardAccounts.hook';
import { CardAccountType, CardAccountTypeColor, KycStatusColor } from '@/features/card-account/enum';
import { Divider } from '@/design-system/components/Divider';
import { LayoutChangeEvent } from 'react-native';
import { Tooltip } from '@/design-system/components/tooltip/Tooltip';
import { RootStackRoutes } from 'types';
import { AccountManagementStackRoutes } from '@/features/account-management/account.navigator';
import { AccountDetailsTabRoutes } from '@/features/account-management/screens/details/type';
import { UniButton } from '@/design-system/components/buttons';
import { CardAccountStackRoutes } from '@/features/card-account/card.navigator';
import { CardAccountTypeFilters, CardKycStatusFilters } from '@/features/card-account/options';
import { DownloadTransactionReportStackRoutes } from '@/features/account-management/screens/download-transaction-report/type';

export interface CardAccountListProps {
  flag?: string;
}

type ColorTypes = keyof typeof CardAccountTypeColor;

export const CardAccountListWebScreen: React.FC<CardAccountListProps> = (props) => {
  const { flag } = props;
  const navigation = useNavigation();
  const navigationAny = useNavigation<any>();
  const { theme } = useDripsyTheme();
  const { t } = useTranslation('card-account');

  const [search, setSearch] = useState<string | undefined>(undefined);
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const { pagination, accounts, isLoading, reload, createCardAccount } = useCardAccounts({
    current,
    pageSize,
    search
  });

  const [outstandingWidth, setOutstandingWidth] = useState(0);
  const handleOutstadingLayout = (event: LayoutChangeEvent) => {
    const width = event.nativeEvent.layout.width;
    if (width > outstandingWidth) {
      setOutstandingWidth(width);
    }
  };

  const funcCreateCardAccount = (data: any, onSuccess?: (data: any) => void, onError?: (error: any) => void) => {
    createCardAccount(data, onSuccess, onError);
  };

  return (
    <PageContainer sx={{ paddingX: '$10', paddingY: '$10' }}>
      <View sx={styles.container}>
        <View sx={{ flexDirection: 'row', justifyContent: 'space-between' }}>
          <H3 bold>{t('card-account::landing-page.title--card-account')}</H3>
          <UniButton
            wrapperSx={{ width: 240 }}
            labelSx={{ color: '$black' }}
            label={t('card-account-management::account-page.button--download-transaction-report')}
            onPress={() =>
              navigationAny.navigate(RootStackRoutes.CardAccount, {
                screen: AccountManagementStackRoutes.DownloadTransactionReport,
                params: {
                  screen: DownloadTransactionReportStackRoutes.DownloadCardAccountTransactionReport
                }
              })
            }
          />
        </View>
        <TextBody semiBold secondary sx={{ mt: '$5' }}>
          {t('card-account::landing-page.text--select-account')}
        </TextBody>
        <PageItem sx={styles.card}>
          <View sx={styles.table}>
            <View sx={styles.operations}>
              <View sx={styles.filters}>
                <UniInput
                  wrapperSx={styles.filter_search}
                  size="default"
                  prefixIcon="search"
                  placeholder={t('card-account::landing-page.text--search-by-account-name-number')}
                  onValueChange={(value) => {
                    setCurrent(1);
                    setSearch(value);
                  }}
                />
              </View>
              {flag == '1' && (
                <UniButton
                  wrapperSx={styles.btn_new}
                  labelSx={{ color: '$black' }}
                  label={t('card-account-management::account-page.button--new-account')}
                  onPress={() => {
                    navigation.navigate(RootStackRoutes.CardAccount, {
                      screen: CardAccountStackRoutes.CreateCardAccount,
                      params: {
                        func: funcCreateCardAccount,
                        refresh: reload
                      }
                    });
                  }}
                />
              )}
            </View>
            <View sx={{ gap: '$4' }}>
              {isLoading && <PageItemSkeleton />}
              {!isLoading &&
                accounts.length > 0 &&
                accounts.map((item, i) => (
                  <Pressable
                    key={i}
                    onPress={() => {
                      if (flag != '1') {
                        window.parent.postMessage({ origin: 'card-account', data: { id: item.id } }, '*');
                      } else {
                        navigation.navigate(RootStackRoutes.AccountManagement, {
                          screen: AccountManagementStackRoutes.Details,
                          params: {
                            screen: AccountDetailsTabRoutes.Card,
                            account: item.id,
                            record: { ...item, cardAccountId: item.id },
                            tab: 1,
                            flag: flag
                          }
                        });
                      }
                    }}>
                    <View sx={{ padding: '$7', gap: '$7', boxShadow: '2px 2px 10px rgba(0, 0, 0, 0.1)', borderRadius: 15 }}>
                      <View sx={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                        <View sx={{ flexDirection: 'row', gap: '$2', alignItems: 'center' }}>
                          <Icon name="account" />
                          <H6 bold>{item.label}</H6>
                          <View sx={{ flexDirection: 'row', ml: '$2', alignItems: 'center', height: '$5' }}>
                            <TextCaption sx={{ color: theme.colors?.$textHyperlink as string }}>
                              {t('card-account::landing-page.text--account-details')}
                            </TextCaption>
                            <Icon name="arrowRightOrange" color={theme.colors?.$textHyperlink as string} />
                          </View>
                        </View>
                        <View sx={{ flexDirection: 'row', gap: '$4', alignItems: 'center' }}>
                          {item.cardAccountKycStatus && (
                            <View
                              sx={{
                                ...styles.tagSx,
                              backgroundColor: theme.colors[`$tag${KycStatusColor[item.cardAccountKycStatus as ColorTypes]}Background`] as string
                              }}>
                              <TextCaption sx={{ color: theme.colors[`$tag${KycStatusColor[item.cardAccountKycStatus as ColorTypes]}Text`] as string }}>
                                {CardKycStatusFilters(t).find((rec) => rec.value === item.cardAccountKycStatus)?.label}
                              </TextCaption>
                            </View>
                          )}
                          {item.autopayEnabled && (
                            <View
                              sx={{
                                ...styles.tagSx,
                                backgroundColor: theme.colors?.$tagGreenBackground as string
                              }}>
                              <TextCaption sx={{ color: theme.colors?.$tagGreenText as string }}>
                                {t('card-account::landing-page.text--utgl-autopay-enabled')}
                              </TextCaption>
                            </View>
                          )}
                          <View
                            sx={{
                              ...styles.tagSx,
                              backgroundColor: theme.colors?.$tagBlackBackground as string
                            }}>
                            <TextCaption sx={{ color: theme.colors?.$tagBlackText as string }}>{item.assetSymbol}</TextCaption>
                          </View>
                          <View
                            sx={{
                              ...styles.tagSx,
                              backgroundColor: theme.colors[`$tag${CardAccountTypeColor[item.type as ColorTypes]}Background`] as string
                            }}>
                            <TextCaption sx={{ color: theme.colors[`$tag${CardAccountTypeColor[item.type as ColorTypes]}Text`] as string }}>
                              {CardAccountTypeFilters(t).find((rec) => rec.value === item.type)?.label}
                            </TextCaption>
                          </View>
                        </View>
                      </View>
                      <Divider />
                      <View sx={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                        <View sx={{ flexDirection: 'row', justifyContent: 'flex-start', width: '40%' }}>
                          <View sx={{ flexDirection: 'row', gap: '$3', alignItems: 'center', flex: 1 }}>
                            <View sx={{ width: 40, alignItems: 'center' }}>
                              <Icon name="cardAccount" width={30} height={22} />
                            </View>
                            <View sx={{ flexDirection: 'column', gap: '$1' }}>
                              <TextBody semiBold sx={{ color: theme.colors?.$textSecondary as string }}>
                                {t('card-account::landing-page.text--card-account-balance')}
                              </TextBody>
                              <H6
                                bold
                                sx={{
                                  color: (item.balance > 0 && (item.type as CardAccountType) === CardAccountType.PREPAID
                                    ? theme.colors?.$tagRedBackground
                                    : theme.colors?.$textThemeSecondary) as string
                                }}>
                                {formatAmountByDecimals({ value: Math.abs(item.balance), suffix: item.assetSymbol })}{' '}
                                {item.balance < 0 && '[CR]'}
                              </H6>
                            </View>
                          </View>
                          {(item.type as CardAccountType) !== CardAccountType.PREPAID && (
                            <View sx={{ flexDirection: 'row', gap: '$3', alignItems: 'center' }} style={{ width: outstandingWidth + 52 }}>
                              <View sx={{ width: 40, alignItems: 'center' }}>
                                <Icon name="cardAccount" width={30} height={22} />
                              </View>
                              <View sx={{ flexDirection: 'column', gap: '$1' }} onLayout={handleOutstadingLayout}>
                                <TextBody semiBold sx={{ color: theme.colors?.$textSecondary as string }}>
                                  {t('card-account::landing-page.text--outstanding-balance')}
                                </TextBody>
                                <H6 bold sx={{ color: theme.colors?.$textThemeSecondary as string }}>
                                  {formatAmountByDecimals({ value: Math.abs(item.outstandingBalance), suffix: item.assetSymbol })}{' '}
                                  {item.outstandingBalance < 0 && '[CR]'}
                                </H6>
                              </View>
                            </View>
                          )}
                        </View>
                        {(item.type as CardAccountType) !== CardAccountType.PREPAID && (
                          <View sx={{ flexDirection: 'column', gap: '$1', width: '40%' }}>
                            <View sx={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                              <TextBody semiBold sx={{ color: theme.colors?.$textSecondary as string }}>
                                {t('card-account::landing-page.text--card-account-limit')}
                              </TextBody>
                              <View sx={{ flexDirection: 'row', gap: '$1', justifyContent: 'flex-end', alignItems: 'center' }}>
                                <H6 bold sx={{ color: theme.colors?.$textThemeSecondary as string }}>
                                  {formatAmountByDecimals({ value: item.availableLimit })}
                                </H6>
                                <TextCaption semiBold sx={{ color: theme.colors?.$textPrimary as string }}>
                                  /
                                </TextCaption>
                                <TextCaption semiBold sx={{ color: theme.colors?.$textPrimary as string }}>
                                  {formatAmountByDecimals({ value: item.limit, suffix: item.assetSymbol })}
                                </TextCaption>
                              </View>
                            </View>
                            <View
                              sx={{
                                width: '100%',
                                height: 6,
                                borderRadius: 50,
                                backgroundColor: theme.colors?.$backgroundInputDisabled as string
                              }}>
                              {item.limit > 0 && item.limit - item.balance > 0 && (
                                <View
                                  sx={{
                                    width: item.balance < 0 ? '100%' : `${((item.limit - item.balance) / item.limit) * 100}%`,
                                    height: 6,
                                    borderRadius: 50,
                                    backgroundColor: theme.colors?.$secondary as string
                                  }}
                                />
                              )}
                            </View>
                            <TextSmall sx={{ color: theme.colors?.$textSecondary as string, textAlign: 'right' }}>
                              {item.limit > 0 && item.limit - item.balance > 0
                                ? item.balance < 0
                                  ? '100%'
                                  : `${Number(((item.limit - item.balance) / item.limit) * 100).toFixed(2)}%`
                                : '0%'}
                            </TextSmall>
                          </View>
                        )}
                      </View>
                      {item.cards.filter((card) => (flag == '0' && card.status != 'CANCELLED') || flag != '0').length > 0 && (
                        <View sx={{ flexDirection: 'column', gap: '$3', alignItems: 'center' }}>
                          {item.cards
                            .filter((card) => (flag == '0' && card.status != 'CANCELLED') || flag != '0')
                            .map((card, i) => (
                              <Pressable
                                key={card.id}
                                onPress={() => {
                                  if (flag != '1') {
                                    window.parent.postMessage({ origin: 'card-account', data: { id: item.id, card: card.id } }, '*');
                                  } else {
                                    navigation.navigate(RootStackRoutes.AccountManagement, {
                                      screen: AccountManagementStackRoutes.Details,
                                      params: {
                                        screen: AccountDetailsTabRoutes.Card,
                                        account: item.id,
                                        record: { ...item, cardAccountId: item.id },
                                        tab: 1,
                                        flag: flag,
                                        options: { card }
                                      }
                                    });
                                  }
                                }}
                                sx={{
                                  width: '100%',
                                  boxShadow: '2px 2px 10px rgba(0, 0, 0, 0.1)',
                                  flexDirection: 'row',
                                  alignItems: 'center',
                                  paddingX: '$5',
                                  paddingY: '$3',
                                  justifyContent: 'space-between',
                                  borderRadius: 5,
                                  backgroundColor: ['PENDING_ACTIVATION'].includes(card?.status) ? '#FDFAED' : undefined,
                                  border: ['PENDING_ACTIVATION'].includes(card?.status) ? '1px solid #DC9B32' : undefined
                                }}>
                                <View sx={{ flexDirection: 'row', gap: '$20', alignItems: 'center' }}>
                                  <View sx={{ flexDirection: 'row', gap: '$3', alignItems: 'center', width: 250 }}>
                                    <Image
                                      sx={styles.imageSx}
                                      source={card.productImageUrl || require('assets/images/worldcard_blank.png')}
                                    />
                                    <TextBody sx={{ color: theme.colors?.$textSecondary as string }}>{card.productName}</TextBody>
                                  </View>
                                  <View sx={{ flexDirection: 'row', gap: '$3', alignItems: 'center' }}>
                                    <TextBody semiBold>{card.embossedName}</TextBody>
                                    <TextCaption
                                      sx={{
                                        backgroundColor: (['PENDING_ACTIVATION'].includes(card?.status)
                                          ? theme.colors?.$accent
                                          : theme.colors?.$backgroundInputDisabled) as string,
                                        color: (['PENDING_ACTIVATION'].includes(card?.status)
                                          ? theme.colors?.$textNavigationActive
                                          : theme.colors?.$navbarTextInactive) as string,
                                        borderRadius: 100,
                                        paddingX: '$2',
                                        paddingY: '$1'
                                      }}>{`**** ${card.last4}`}</TextCaption>
                                  </View>
                                </View>
                                <View sx={{ flexDirection: 'row', gap: '$20', alignItems: 'center' }}>
                                  <View sx={{ flexDirection: 'row', gap: '$3', alignItems: 'center', justifyContent: 'flex-end' }}>
                                    <TextSmall sx={{ color: theme.colors?.$textSecondary as string }}>
                                      {t('card-account::landing-page.text--todays-spending')}
                                    </TextSmall>
                                    <TextBody semiBold sx={{ color: theme.colors?.$textThemeSecondary as string }}>
                                      {formatAmountByDecimals({ value: card.cardSpendingToday, suffix: item.assetSymbol })}
                                    </TextBody>
                                  </View>
                                  <View sx={{ flexDirection: 'row', justifyContent: 'center', gap: '$12' }}>
                                    {['LOCKED', 'SUSPENDED'].includes(card?.status) ? (
                                      <Tooltip
                                        content={
                                          <TextBody sx={styles.tooltipText}>
                                            {card?.status == 'LOCKED'
                                              ? t('card-account-management::lock-card.text--card-locked')
                                              : t('card-account::account-page.title--card-suspended')}
                                          </TextBody>
                                        }
                                        position="middle"
                                        contentTextMinWidth={106}
                                        showUnderline={false}>
                                        <Icon name={card?.status == 'LOCKED' ? 'lock1' : 'cardSuspended'} />
                                      </Tooltip>
                                    ) : (
                                      <View sx={{ width: '$5' }} />
                                    )}
                                    <View sx={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', height: '$5' }}>
                                      <TextCaption sx={{ color: theme.colors?.$textHyperlink as string }}>
                                        {['PENDING_ACTIVATION'].includes(card?.status)
                                          ? t('card::main-card.view-all-cards.button--activate-card')
                                          : t('card-account::landing-page.text--card-details')}
                                      </TextCaption>
                                      <Icon name="arrowRightOrange" color={theme.colors?.$textHyperlink as string} />
                                    </View>
                                  </View>
                                </View>
                              </Pressable>
                            ))}
                        </View>
                      )}
                    </View>
                  </Pressable>
                ))}
            </View>
          </View>
          <View sx={styles.footer}>
            <Pagination
              totalPages={pagination.totalPages}
              currentPage={pagination.current}
              onNextPage={() => {
                window.scrollTo(0, 0);
                setCurrent((prev) => prev + 1);
              }}
              onPrevPage={() => {
                window.scrollTo(0, 0);
                setCurrent((prev) => prev - 1);
              }}
            />
          </View>
        </PageItem>
      </View>
    </PageContainer>
  );
};

enum StyleName {
  container = 'container',
  card = 'card',
  table = 'table',
  operations = 'operations',
  filters = 'filters',
  filter_search = 'filter_search',
  footer = 'footer',
  imageSx = 'imageSx',
  tagSx = 'tagSx',
  tooltipText = 'tooltipText',
  btn_new = 'btn_new'
}

const styles: Record<StyleName, SxProp> = {
  container: {
    flexDirection: 'column',
    height: '100%'
  },
  card: {
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: '$8',
    marginTop: '$8',
    gap: '$8',
    borderRadius: 15
  },
  table: {
    flexDirection: 'column',
    gap: '$8'
  },
  operations: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    zIndex: 1
  },
  filters: {
    flexDirection: 'row',
    gap: '$5'
  },
  filter_search: {
    minWidth: 400
  },
  footer: { flexDirection: 'row', justifyContent: 'flex-end' },
  imageSx: { width: 40, height: 24 },
  tagSx: { paddingX: '$2', paddingY: '$1', width: 'fit-content', borderRadius: 100 },
  tooltipText: {
    color: '$textTooltip',
    whiteSpace: 'nowrap',
    textAlign: 'center'
  },
  btn_new: {
    width: 200
  }
};
